import { adoptUserLanguage } from "../i18n";
import api, { storeTokens, USER_KEY } from "./api";
import { fetchMe, LOGIN_PLATFORM } from "./authService";

/**
 * GET /public/plans — the plans the pricing page offers (no auth), the public
 * billing cycles, and the cycle to pre-select. Every price comes computed in
 * `plan.prices[cycleId]`; the portal never works one out itself.
 *
 * The currency is decided by the server, never here: the business's country
 * when there is one (signed in, or `countryId` = the country being registered
 * in at sign-up), else the visitor's IP country, else each plan's default.
 * `currencySource` says which (business_country | ip | default).
 */
export const fetchPublicPlans = async ({ countryId } = {}) => {
  const { data } = await api.get("/public/plans", {
    params: countryId ? { country_id: countryId } : undefined,
  });
  return {
    plans: data.data.plans,
    billingCycles: data.data.billing_cycles ?? [],
    defaultCycleId: data.data.default_billing_cycle_id ?? null,
    currencySource: data.data.currency_source ?? "default",
    country: data.data.country ?? null,
  };
};

/**
 * POST /public/promo-codes/validate — the plan's quote with the code applied,
 * or a 422 on `promo_code` (or `billing_cycle_id` / `plan_id`). Nothing is
 * redeemed until the sign-up itself.
 */
export const validatePromoCode = async ({ code, planId, cycleId, countryId }) => {
  const { data } = await api.post("/public/promo-codes/validate", {
    code,
    plan_id: planId,
    billing_cycle_id: cycleId,
    // The country being registered in decides the currency, as it will at sign-up.
    ...(countryId ? { country_id: Number(countryId) } : {}),
  });
  return data.data.quote;
};

/**
 * The currencies some plans fell back to because the visitor's own currency
 * is not priced (currency_source "default"), e.g. ["USD"] — for the small
 * "Prices shown in USD" note. Empty when every plan is in the visitor's currency.
 */
export const defaultCurrencyCodes = (plans) => [
  ...new Set(
    (plans ?? []).filter((p) => p.currency_source === "default" && p.currency).map((p) => p.currency.code)
  ),
];

/** The cycle to show for a plan: the wanted one when the plan offers it, else the first it does. */
export const pickCycle = (plan, wanted, fallback) => {
  const offered = Object.keys(plan?.prices ?? {});
  if (wanted && offered.includes(wanted)) return wanted;
  if (fallback && offered.includes(fallback)) return fallback;
  return offered[0] ?? null;
};

/** GET /public/business-types — the types a new business can pick (no auth). */
export const fetchPublicBusinessTypes = async () => {
  const { data } = await api.get("/public/business-types");
  return data.data.business_types;
};

/** The sign-up step after the form: GET/POST /business/signup/plan. */
export const SIGNUP_PENDING_PLAN = "pending_plan";

/** Whether the signed-in owner still has to confirm the sign-up's plan. */
export const awaitingPlanStep = (user) => user?.business?.signup_status === SIGNUP_PENDING_PLAN;

/**
 * POST /public/business-signup — step 1. No tokens any more: the business
 * waits for the emailed 6-digit code. Resolves with
 * { email, codeSent, resendIn, expiresIn }. A 409 `signup_pending_verification`
 * (this email already started) is thrown like any error; see pendingSignupOf().
 */
export const signupBusiness = async (payload) => {
  const { data } = await api.post("/public/business-signup", { ...payload, platform: LOGIN_PLATFORM });
  return {
    email: data.email,
    codeSent: data.code_sent,
    resendIn: data.resend_available_in ?? 60,
    expiresIn: data.expires_in ?? null,
  };
};

/**
 * The verification screen's state when an error means "this email has a
 * sign-up waiting for its code": a 409 signup_pending_verification from
 * sign-up, or a 403 email_unverified from login. Null otherwise.
 */
export const pendingSignupOf = (err) => {
  const data = err?.response?.data;
  if (!data || !["signup_pending_verification", "email_unverified"].includes(data.code)) return null;
  return {
    email: data.email,
    reason: data.code === "signup_pending_verification" ? "pending" : "unverified",
    codeSent: !!data.code_sent,
    resendIn: data.resend_available_in ?? 0,
    expiresIn: data.expires_in ?? null,
  };
};

/**
 * POST /business/signup/verify — step 2. A right code signs the owner in
 * (same token pair as a login) and the plan step comes next.
 */
export const verifySignupCode = async (email, code) => {
  const { data } = await api.post("/business/signup/verify", { email, code, platform: LOGIN_PLATFORM });
  storeTokens(data);

  const user = await fetchMe();
  adoptUserLanguage(user);
  return { user };
};

/** POST /business/signup/resend — a new code (60 s cooldown, hourly caps → 429). */
export const resendSignupCode = async (email) => {
  const { data } = await api.post("/business/signup/resend", { email });
  return { codeSent: data.code_sent, resendIn: data.resend_available_in ?? 60, expiresIn: data.expires_in ?? null };
};

/** GET /business/signup/plan — what was picked on the sign-up form. */
export const fetchSignupPlan = async () => {
  const { data } = await api.get("/business/signup/plan");
  return {
    planId: data.data.requested.plan_id,
    cycleId: data.data.requested.billing_cycle_id,
    promoCode: data.data.requested.promo_code,
  };
};

/**
 * POST /business/signup/plan — step 3: runs the subscription (trial starts /
 * free active / setup mode + pending payment). Resolves with the fresh profile.
 */
export const confirmSignupPlan = async ({ planId, cycleId, promoCode }) => {
  const { data } = await api.post("/business/signup/plan", {
    plan_id: planId,
    billing_cycle_id: cycleId || null,
    promo_code: promoCode || null,
  });
  const user = data.data.business_user;
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  // No "just signed up" flag: the dashboard's getting-started banner comes from
  // GET /business/onboarding for whichever business is signed in.
  return { user };
};

/** A plan's text in the current language, falling back to English. */
export const planText = (value, language) => (value ? value[language] || value.en || "" : "");
